// SPDX-License-Identifier: LicenseRef-Proprietary

import { AbstractTriageBrief, PotentialInputSource } from '../../../lib/models';
import { INPUT_SOURCE_LABELS } from '../constants/triageConstants';

export interface SummaryStats {
  totalAnalyzed: number;
  inputSourceCounts: Record<PotentialInputSource, number>;
  confidenceCounts: Record<'high' | 'medium' | 'low', number>;
  yearRange?: { min: number; max: number };
  verbalSummary?: string;
  
  // NEW FOR V2
  retrievableInputCounts: Record<string, number>;
  finlandCounts: { high: number; medium: number; low: number; unknown: number };
}

/**
 * Generates a human-readable verbal summary based on aggregate data
 */
export function generateVerbalSummary(stats: Omit<SummaryStats, 'verbalSummary'>): string {
  const { totalAnalyzed, inputSourceCounts, yearRange } = stats;
  
  if (totalAnalyzed === 0) return '';

  const years = yearRange 
    ? (yearRange.min === yearRange.max ? `in ${yearRange.min}` : `between ${yearRange.min} and ${yearRange.max}`)
    : '';

  const sortedSources = (Object.entries(inputSourceCounts) as [PotentialInputSource, number][])
    .filter(([source, count]) => count > 0 && source !== 'none')
    .sort(([_, a], [__, b]) => b - a);

  let sourceText = '';
  if (sortedSources.length > 0) {
    const top3 = sortedSources.slice(0, 3).map(([source]) => INPUT_SOURCE_LABELS[source]?.toLowerCase() || source);
    if (top3.length === 1) {
      sourceText = `, primarily focusing on ${top3[0]}`;
    } else if (top3.length === 2) {
      sourceText = `, primarily focusing on ${top3[0]} and ${top3[1]}`;
    } else {
      sourceText = `, with significant focus on ${top3[0]}, ${top3[1]}, and ${top3[2]}`;
    }
  }

  return `Analyzed ${totalAnalyzed} paper${totalAnalyzed === 1 ? '' : 's'} ${years}${sourceText}.`;
}

/**
 * Aggregates AI triage results into high-level statistics
 * @param briefs Map of PMIDs to triage briefs
 * @param papers Array of paper summaries for metadata like years
 */
export function aggregateTriageData(
  briefs: Record<string, AbstractTriageBrief>,
  papers: Array<{ pmid: string; year?: number }>
): SummaryStats {
  const briefValues = Object.values(briefs);
  const totalAnalyzed = briefValues.length;
  
  const inputSourceCounts: Record<PotentialInputSource, number> = {
    vaccineEfficacy: 0,
    waningBoosters: 0,
    incidenceEpi: 0,
    costsResourceUse: 0,
    utilitiesQoL: 0,
    safety: 0,
    coverageAdherence: 0,
    modelStructure: 0,
    none: 0,
  };

  const confidenceCounts: Record<'high' | 'medium' | 'low', number> = {
    high: 0,
    medium: 0,
    low: 0,
  };

  const retrievableInputCounts: Record<string, number> = {};
  const finlandCounts = { high: 0, medium: 0, low: 0, unknown: 0 };

  briefValues.forEach(brief => {
    // Input sources
    brief.potentialInputSources.forEach((source: PotentialInputSource) => {
      if (inputSourceCounts[source] !== undefined) {
        inputSourceCounts[source]++;
      }
    });

    // Confidence
    const conf = brief.confidence;
    if (conf && confidenceCounts[conf] !== undefined) {
      confidenceCounts[conf]++;
    }

    // Retrievable inputs (v2)
    brief.retrievable_inputs?.forEach(input => {
      if (["explicit_in_abstract", "likely_in_full_text"].includes(input.likelihood)) {
        retrievableInputCounts[input.input_key] = (retrievableInputCounts[input.input_key] ?? 0) + 1;
      }
    });

    // Finland applicability (v2)
    const applicability = brief.finland_applicability_overall || "unknown";
    if (finlandCounts[applicability] !== undefined) {
      finlandCounts[applicability]++;
    }
  });

  // Year range
  const analyzedPmids = new Set(Object.keys(briefs));
  const years = papers
    .filter(p => analyzedPmids.has(p.pmid) && p.year)
    .map(p => p.year as number);

  const yearRange = years.length > 0 
    ? { min: Math.min(...years), max: Math.max(...years) }
    : undefined;

  const statsWithoutSummary = {
    totalAnalyzed,
    inputSourceCounts,
    confidenceCounts,
    yearRange,
    retrievableInputCounts,
    finlandCounts,
  };

  return {
    ...statsWithoutSummary,
    verbalSummary: generateVerbalSummary(statsWithoutSummary),
  };
}
